import {
	OrderPriceChangeReason,
	type OrderPriceResponseDto,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { createSelectOptions } from "Infrastructure/Utilities/Helpers/createSelectOptions";
import { useOrderPriceEditForm } from "Order/Modules/Price/Components/Form/Hooks/useOrderPriceEditForm";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import Select from "Shared/Components/Form/CustomFormComponent/Select";
import { Button, Grid2, Stack } from "@mui/material";
import { useMemo } from "react";

const { editForm, selectOptions } = Resources.base.order;

type OrderPriceEditFormProps = {
	orderPrice: OrderPriceResponseDto;
	orderId: number;
	isMaterialPrice?: boolean;
	setIsPriceModalOpen?: (isOpen: boolean) => void;
};

export function OrderPriceEditForm({
	orderPrice,
	orderId,
	isMaterialPrice: isFlooring,
	setIsPriceModalOpen,
}: OrderPriceEditFormProps) {
	const { t } = useResource();
	const { form, isPending, handleSubmitUpdate } = useOrderPriceEditForm(
		orderPrice,
		orderId,
		setIsPriceModalOpen,
	);

	const {
		control,
		formState: { errors, isDirty },
	} = form;

	const priceChangeReasonOptions = useMemo(() => {
		return createSelectOptions(
			OrderPriceChangeReason,
			t,
			selectOptions.orderPriceChangeReason,
		).filter(
			(option) =>
				![
					OrderPriceChangeReason.website_price,
					OrderPriceChangeReason.manually_created_order,
				].includes(option.value),
		);
	}, [selectOptions.orderPriceChangeReason]);

	return (
		<>
			<Grid2 container columnSpacing={2}>
				<Grid2 size={isFlooring ? { xs: 12, sm: 6 } : { xs: 12 }}>
					<Input
						id="price"
						name="price"
						label={t(editForm.price)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						isNumber
						isFloat
					/>
				</Grid2>
				{isFlooring && (
					<Grid2 size={{ xs: 12, sm: 6 }}>
						<Input
							id="materialPrice"
							name="materialPrice"
							label={t(editForm.materialPrice)}
							variant="outlined"
							control={control}
							errors={errors}
							fullWidth
							isNumber
							isFloat
						/>
					</Grid2>
				)}
				<Grid2 size={isFlooring ? { xs: 12, sm: 6 } : 12}>
					<Select
						id="reason"
						name="reason"
						label={t(editForm.priceChangeReason)}
						variant="outlined"
						control={control}
						errors={errors}
						options={priceChangeReasonOptions}
						fullWidth
					/>
				</Grid2>
			</Grid2>
			<Stack spacing={1} direction="row" mt={2} justifyContent="flex-end">
				{setIsPriceModalOpen && (
					<Button onClick={() => setIsPriceModalOpen(false)} variant="outlined">
						{t(editForm.cancel)}
					</Button>
				)}
				<Button
					loading={isPending}
					onClick={() => {
						handleSubmitUpdate();
					}}
					variant="contained"
					disabled={!isDirty}
				>
					{t(editForm.save)}
				</Button>
			</Stack>
		</>
	);
}
